/**
 * Training data: DEV ONLY (Jeremy, 21 Oct 2026). Wipes the scheduler's work data on dev and books
 * a realistic month for real people (api/src/hr/training-data.ts). Hidden everywhere the API says
 * it is not enabled, which is everywhere but scheduler_dev; the API re-checks on the POST.
 */
import { useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";

type Result = Awaited<ReturnType<ReturnType<typeof useSchedulerApi>["trainingDataLoad"]>>;

export function TrainingData() {
  const api = useSchedulerApi();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.trainingDataEnabled().then(setEnabled, () => setEnabled(false));
  }, [api]);

  if (!enabled) return null;

  async function load() {
    if (!window.confirm("This DELETES every request, deployment and WFH day on dev, then books the training data. Go ahead?")) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.trainingDataLoad());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The training data could not be loaded.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h3>Training data (dev only)</h3>
      <p className="hint">
        Clears every request, deployment and WFH day on this dev environment, then books a realistic
        month for real people: approved, pending and denied PTO, jury duty, no-travel days, and field
        work that is booked and cancelled. Load it before the projects' training data (Canopy's Admin page),
        which links its field tasks to these deployments.
      </p>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <button type="button" className="primary" disabled={busy} onClick={() => void load()}>
        {busy ? "Loading…" : "Load training data"}
      </button>
      {result ? (
        <div className="hint" role="status">
          <p>
            Loaded {result.requests} requests and {result.deployments} deployments
            {result.balancesAdded ? `, and gave ${result.balancesAdded} people a PTO balance` : ""}.
          </p>
          {result.refused.length ? (
            <ul>{result.refused.map((r) => <li key={r}>{r}</li>)}</ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
